/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, PublishReleaseResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';

export type PublishReleaseMutationVariables = {
  releaseId: string;
  tenantIds: string[];
};

export type PublishReleaseMutationConfig = {
  onSuccess?: (data: PublishReleaseResponse, variables: PublishReleaseMutationVariables) => void;
  onError?: (error: unknown, variables: PublishReleaseMutationVariables) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: PublishReleaseMutationVariables) => Promise<unknown> | void;
};

/**
 * Hook for publishing a release to one or more tenants.
 *
 * @param config.onSuccess - Called after successful release publication
 * @param config.onError - Called if release publication fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @example
 * ```tsx
 * const { mutate } = usePublishReleaseMutation({
 *   onSuccess: (data) => console.log('Release published:', data),
 *   onError: (error) => console.error('Publication failed:', error)
 * });
 *
 * // Publish release to a tenant
 * mutate({ releaseId: 'release-uuid', tenantIds: ['tenant-uuid'] });
 * ```
 */
export function usePublishReleaseMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: PublishReleaseMutationConfig = {}) {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<
    PublishReleaseResponse,
    ApiError,
    PublishReleaseMutationVariables
  >({
    mutationFn: async ({ releaseId, tenantIds }: PublishReleaseMutationVariables) => {
      const response = await mgmtPlaneAPIClient.releases.publishRelease({
        releaseId,
        requestBody: { tenantIds },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate tenant releases queries to refresh the published status
      queryClient.invalidateQueries({ queryKey: ['tenant-releases'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-releases-by-os'] });
      queryClient.invalidateQueries({ queryKey: ['all-tenant-releases'] });
      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error, isPending };
}
